import React, { useState, useEffect } from "react";
import {
  ExternalLink,
  MapPin,
  AlertCircle,
  Clock,
  Calendar,
} from "lucide-react";
import { API_BASE_URL, API_ENDPOINTS } from "../../constants/index";

const Modal = ({ isOpen, onClose, title, children, size = "md" }) => {
  if (!isOpen) return null;

  const sizes = {
    sm: "max-w-md",
    md: "max-w-lg",
    lg: "max-w-2xl",
    xl: "max-w-4xl",
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        ></div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">
          &#8203;
        </span>

        <div
          className={`inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:w-full ${sizes[size]}`}
        >
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">{title}</h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 focus:outline-none"
              >
                <span className="sr-only">Close</span>
                <svg
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

const MapViewer = ({ url, legid, title, height, showControls }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [fastTagData, setFastTagData] = useState([]);
  const [showFastTagModal, setShowFastTagModal] = useState(false);
  const [legDetails, setLegDetails] = useState(null);

  const [fastTagLoading, setFastTagLoading] = useState(false);
  const [fastTagError, setFastTagError] = useState(null);
  const abortRef = React.useRef(null); // to hold AbortController

  const handleIframeLoad = () => {
    setIsLoading(false);
    setHasError(false);
  };

  const handleIframeError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  const openInNewTab = () => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const fastTag_detail_handler = async () => {
    // open modal immediately for instant feedback
    setFastTagError(null);
    setFastTagData([]);
    setShowFastTagModal(true);
    setFastTagLoading(true);

    // cancel any previous request
    if (abortRef.current) {
      abortRef.current.abort();
    }
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const url = new URL(
        API_BASE_URL + API_ENDPOINTS.FASTTAGTRACKINGBYID + "/" + legid
      );
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
        signal: controller.signal,
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(`HTTP ${response.status}: ${text}`);
      }

      const res = await response.json();
      setFastTagData(res.data || []);
      setFastTagError(null);
    } catch (error) {
      if (error.name === "AbortError") {
        console.log("FastTag request aborted");
      } else {
        console.error("Error fetching FastTag details:", error);
        setFastTagError(error.message || "Failed to fetch FastTag details");
        setFastTagData([]);
      }
    } finally {
      setFastTagLoading(false);
      abortRef.current = null;
    }
  };

  const fetchLegDetails = async () => {
    try {
      const url = `${API_BASE_URL}${API_ENDPOINTS.LEGSDETAILS}${legid}`;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const res = await response.json();
      setLegDetails(res.data);
      return res.data;
    } catch (error) {
      console.error("Error fetching leg details:", error);
      return null;
    }
  };

  const fetchRoute = async () => {
    let legData = legDetails;

    if (!legData) {
      legData = await fetchLegDetails();
    }

    if (!legData || !legData.originPlaceData || !legData.destinationPlaceData) {
      alert("Origin and destination data not available for this leg.");
      return;
    }

    const originData = legData.originPlaceData;
    const destinationData = legData.destinationPlaceData;

    if (
      !originData.lat ||
      !originData.lng ||
      !destinationData.lat ||
      !destinationData.lng
    ) {
      alert("Origin or destination coordinates are missing.");
      return;
    }

    try {
      const API_KEY = import.meta.env.REACT_APP_GOOGLE_MAPS_API_KEY;

      if (!API_KEY) {
        throw new Error("Google Maps API key not configured");
      }

      const routesUrl = `https://routes.googleapis.com/directions/v2:computeRoutes?key=${API_KEY}`;

      const body = {
        origin: {
          location: {
            latLng: {
              latitude: parseFloat(originData.lat),
              longitude: parseFloat(originData.lng),
            },
          },
        },
        destination: {
          location: {
            latLng: {
              latitude: parseFloat(destinationData.lat),
              longitude: parseFloat(destinationData.lng),
            },
          },
        },
        travelMode: "DRIVE",
        routingPreference: "TRAFFIC_AWARE",
        polylineQuality: "HIGH_QUALITY",
        extraComputations: ["TRAFFIC_ON_POLYLINE"],
      };

      const response = await fetch(routesUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Goog-FieldMask":
            "routes.duration,routes.distanceMeters,routes.polyline.encodedPolyline,routes.legs.travelAdvisory.speedReadingIntervals",
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.routes && data.routes.length > 0) {
        const route = data.routes[0];
        const duration = route.duration
          ? `${Math.round(parseFloat(route.duration.replace("s", "")) / 60)} minutes`
          : "N/A";
        const distance = route.distanceMeters
          ? `${(route.distanceMeters / 1000).toFixed(1)} km`
          : "N/A";
        alert(
          `Traffic data fetched successfully!\n\nRoute: ${originData.name} → ${destinationData.name}\nDuration: ${duration}\nDistance: ${distance}`
        );
      } else {
        alert("Traffic data fetched but no routes found.");
      }
    } catch (error) {
      console.error("Error fetching traffic data:", error);
      alert(`Failed to fetch traffic data: ${error.message}`);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }),
      time: date.toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      }),
    };
  };

  // when closing modal
  const closeFastTagModal = () => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setShowFastTagModal(false);
    setFastTagLoading(false);
    setFastTagError(null);
    // optionally keep data or clear it:
    // setFastTagData([]);
  };

  return (
    <div className="w-full mx-auto bg-white rounded-lg shadow-lg overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <MapPin className="h-6 w-6" />
            <h2 className="text-xl font-semibold">{title}</h2>
          </div>
          {showControls && (
            <div className="flex items-center">
              <button
                onClick={fastTag_detail_handler}
                className="flex me-3 items-center space-x-2 bg-blue-500 hover:bg-blue-400 transition-colors duration-200 px-4 py-2 rounded-lg text-sm font-medium"
              >
                <ExternalLink className="h-4 w-4" />
                <span>FastTag Details</span>
              </button>
              <button
                onClick={openInNewTab}
                className="flex items-center space-x-2 bg-blue-500 hover:bg-blue-400 transition-colors duration-200 px-4 py-2 rounded-lg text-sm font-medium"
              >
                <ExternalLink className="h-4 w-4" />
                <span>Open in New Tab</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Map Container */}
      <div className="relative" style={{ height }}>
        {/* Loading State */}
        {isLoading && (
          <div className="absolute inset-0 bg-gray-100 flex items-center justify-center z-10">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading map...</p>
            </div>
          </div>
        )}

        {/* Error State */}
        {hasError && (
          <div className="absolute inset-0 bg-gray-50 flex items-center justify-center z-10">
            <div className="text-center">
              <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <p className="text-gray-600 mb-4">Failed to load map</p>
              <button
                onClick={openInNewTab}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors duration-200"
              >
                Open in Browser
              </button>
            </div>
          </div>
        )}

        {/* Iframe */}
        <iframe
          src={url}
          className="w-full h-full border-0"
          onLoad={handleIframeLoad}
          onError={handleIframeError}
          title={title}
          allow="geolocation"
          loading="lazy"
        />
      </div>
      {/* Fast tag Modal view */}
      <Modal
        isOpen={showFastTagModal}
        onClose={closeFastTagModal}
        title="Fast Tag Detail"
        size="lg"
      >
        <div className="space-y-4">
          {fastTagLoading && (
            <div className="py-8 flex flex-col items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
              <p className="text-gray-600">Loading FastTag details...</p>
            </div>
          )}

          {!fastTagLoading && fastTagError && (
            <div className="py-6 text-center text-red-600">
              <p className="mb-3">Failed to load FastTag details</p>
              <p className="text-sm text-gray-600">{fastTagError}</p>
              <div className="mt-4">
                <button
                  onClick={fastTag_detail_handler}
                  className="px-4 py-2 bg-blue-600 text-white rounded"
                >
                  Retry
                </button>
              </div>
            </div>
          )}

          {!fastTagLoading && !fastTagError && fastTagData?.length === 0 && (
            <div className="py-6 text-center text-gray-600">
              No FastTag transactions available for this leg.
            </div>
          )}

          {!fastTagLoading && !fastTagError && fastTagData?.length > 0 && (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    <div className="flex items-center space-x-2">
                      <MapPin className="w-4 h-4" />
                      <span>Location Details</span>
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    <div className="flex items-center space-x-2">
                      <Clock className="w-4 h-4" />
                      <span>Transaction Time</span>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {fastTagData.map((data, index) => {
                  const formattedDate = formatDate(data.timestamp);
                  return (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-start space-x-3">
                          <div className="flex-shrink-0 mt-1">
                            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                              <MapPin className="w-4 h-4 text-blue-600" />
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {data.detailedAddress}
                            </p>
                            <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                              {data.fullAddress}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-start space-x-3">
                          <div className="flex-shrink-0 mt-1">
                            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                              <Clock className="w-4 h-4 text-green-600" />
                            </div>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {formattedDate.date}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              {formattedDate.time}
                            </p>
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </Modal>
    </div>
  );
};

// Fixed MapView component
const MapView = ({ legId, mapUrl, ...props }) => {
  const [currentUrl, setCurrentUrl] = useState(mapUrl);

  useEffect(() => {
    // Create an async function inside useEffect
    const fetchTripUrl = async () => {
      try {
        console.log(legId);
        const url = `${API_BASE_URL}${API_ENDPOINTS.TRACKINGBYID}${legId}`;
        console.log("Fetching from URL:", url);
        const response = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        });

        console.log("Response status:", response.status);
        if (!response.ok) {
          const errorText = await response.text();
          console.error("API Error Response:", errorText);
          throw new Error(
            `HTTP error! status: ${response.status} - ${errorText}`
          );
        }

        const res = await response.json();
        console.log(`Response from API:`, res.data);
        setCurrentUrl(res.data.trip_url);
      } catch (error) {
        console.error("Error fetching trip URL:", error);
        // Optionally handle error state here
      }
    };

    // Only fetch if legId exists
    if (legId) {
      fetchTripUrl();
    }

    // Optional cleanup function (if needed)
    return () => {
      // Any cleanup logic here
    };
  }, [legId]); // Add legId as dependency

  return (
    <div className="p-1">
      <div className="mx-auto">
        {/* Map Viewer */}
        <MapViewer
          url={currentUrl}
          legid={legId}
          title="Trip Tracker Map"
          height="500px"
          showControls={true}
        />
      </div>
    </div>
  );
};

export default MapView;
